import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Card } from '@lib/ui';
import { setThisClass } from '@lib/utils';

export type StatCardColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export interface StatCardData {
  /** Unique identifier */
  id: string;
  /** Card title/label */
  title: string;
  /** Main value to display */
  value: string | number;
  /** Trend percentage (positive or negative) */
  trend?: number;
  /** Icon to display */
  icon: ReactNode;
  /** Color theme for the card */
  color?: StatCardColor;
}

export interface StatCardsProps {
  /** Array of stat card data */
  cards: StatCardData[];
  /** Number of columns on different screen sizes */
  columns?: {
    sm?: 1 | 2 | 3 | 4;
    md?: 1 | 2 | 3 | 4;
    lg?: 1 | 2 | 3 | 4;
  };
  /** Additional className */
  className?: string;
}

const TrendIcon = ({ direction }: { direction: 'up' | 'down' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className={clsx('size-4', direction === 'down' && 'rotate-180')}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
  </svg>
);

export function StatCards({ cards, columns = { sm: 2, lg: 4 }, className }: StatCardsProps) {
  const gridCols = clsx(
    'grid grid-cols-1 gap-4',
    columns.sm === 2 && 'sm:grid-cols-2',
    columns.sm === 3 && 'sm:grid-cols-3',
    columns.sm === 4 && 'sm:grid-cols-4',
    columns.md === 2 && 'md:grid-cols-2',
    columns.md === 3 && 'md:grid-cols-3',
    columns.md === 4 && 'md:grid-cols-4',
    columns.lg === 2 && 'lg:grid-cols-2',
    columns.lg === 3 && 'lg:grid-cols-3',
    columns.lg === 4 && 'lg:grid-cols-4',
    'sm:gap-5 lg:gap-6'
  );

  return (
    <div className={clsx(gridCols, className)}>
      {cards.map((card) => (
        <StatCard key={card.id} {...card} />
      ))}
    </div>
  );
}

function StatCard({ title, value, trend, icon, color = 'primary' }: StatCardData) {
  const trendDirection = trend && trend >= 0 ? 'up' : 'down';
  const trendColor = trendDirection === 'up' ? 'success' : 'error';

  return (
    <Card className="flex justify-between p-5">
      <div>
        <p className="text-gray-600 dark:text-dark-200">{title}</p>
        <p
          className={clsx(
            setThisClass(color),
            'text-this dark:text-this-lighter mt-0.5 text-2xl font-medium'
          )}
        >
          {value}
        </p>
        {trend !== undefined && (
          <p
            className={clsx(
              setThisClass(trendColor),
              'text-this dark:text-this-lighter mt-3 flex items-center gap-1'
            )}
          >
            <TrendIcon direction={trendDirection} />
            <span>{Math.abs(trend)}%</span>
          </p>
        )}
      </div>
      <div className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-this-darker/10 text-this-darker dark:bg-this-lighter/10 dark:text-this-lighter">
        {icon}
      </div>
    </Card>
  );
}

StatCards.displayName = 'StatCards';
